import {
  BookingEngineServiceDTO,
  PreCheckInReservationItemServiceDTO,
  ReservationItemServiceDTO,
  GuestDTO,
  BookerDTO,
  ApiDropdown,
  RoomTypeAvailabilityDTO,
  RoomTypeDTO,
  CreditCardDTO,
  RateDTO,
  RoomTypeUpgradeDTO,
  CardDetailsDTO,
  IndividualConfigurationDTO,
  NightlyRateDTO,
  TagDTO,
  PolicyDTO,
  RoomRatePromotionViewDTO,
} from 'src/services/api/data-contracts';

import useValidationUtils from 'src/composables/validationUtils';
const validationUtils = useValidationUtils();
import useUtils from 'src/composables/utils';
const utils = useUtils();

const currencies = [
  'GBP',
  'EUR',
  'USD',
  'AUD',
  'ZAR',
  'CAD',
  'MXN',
  'NGN',
  'DKK',
  'AED',
  'KES',
] as const;
export type Currency = (typeof currencies)[number];

export class CreditCard {
  constructor() {
    this.saveCardForFutureUse = false;
  }

  creditCardId?: number;

  creditCardName: string;
  creditCardNumber: string;
  cardHolderEmail: string;
  expiryMonth: number;
  expiryYear: number;
  cvvNumber: string;
  cardType: IApiDropdownOption;
  saveCardForFutureUse: boolean;

  get isValid(): boolean {
    return (
      !!this.creditCardName &&
      !!this.creditCardNumber &&
      !!this.cvvNumber &&
      this.expiryMonth != 0 &&
      this.expiryMonth != null &&
      this.expiryYear != 0 &&
      this.expiryYear != null &&
      this.cardType != null
    );
  }

  get isEmailValid(): boolean {
    return !!this.cardHolderEmail;
  }

  toDTO(): CardDetailsDTO {
    return {
      CreditCardId: this.creditCardId,
      SaveCreditCardToIndividual: this.saveCardForFutureUse,

      CardNumber: this.creditCardNumber,
      NameOnCard: this.creditCardName,
      EmailAddress: this.cardHolderEmail,
      CreditCardTypeId: this.cardType?.Id,
      ExpiryMonth: this.expiryMonth,
      ExpiryYear: this.expiryYear,
      SecurityCode: this.cvvNumber,
    } as CardDetailsDTO;
  }
}

export class UserDTO {
  emailAddress: string;
  firstName: string;
  lastName: string;
  telephoneNumber: string;
  receiveBrandOffers: boolean;
  canChoosePaymentMethod: boolean;
  creditCards: CreditCardDTO[];
}

export class IApiDropdownOption {
  Id: number;
  Text: string;
}

export interface IPropertyTheme {
  primary: string;
  secondary: string;
  accent: string;

  dark: string;
  darkPage: string;

  positive: string;
  negative: string;
  info: string;
  warning: string;

  lightPage: string;
  brandKey: string;
  brandBackground: string;
}

export interface IProblemDetails {
  type?: string;
  title: string;
  traceId?: string;
  status: number;
  errors?: IProblemDetailErrors;
  detail: string;
}

export interface IProblemDetailErrors {
  '': Array<string>;
  [key: string]: Array<string>;
}

export class Rate implements RateDTO {
  constructor(data: RateDTO) {
    Object.assign(this, data);

    this.DailyRates = data.DailyRates;
    this.OriginalDailyRates = data.OriginalDailyRates;

    const baseNumberOfAdultsValues = this.DailyRates.map(
      (v) => v.BaseNumberOfAdults,
    );

    const nonNullValues = baseNumberOfAdultsValues.filter((val) => {
      return val !== null;
    });

    const minBaseNumberOfAdultsValue =
      nonNullValues.length > 0 ? Math.min(...nonNullValues) : null;

    this.BaseNumberOfAdults = minBaseNumberOfAdultsValue;

    this.BaseRate = this.DailyRates.reduce((t, v) => t + v.BaseRate, 0);
    this.CostPerRoom = this.DailyRates.reduce((t, v) => t + v.CostPerRoom, 0);
    this.CostPerAdult = this.DailyRates.reduce((t, v) => t + v.CostPerAdult, 0);
    this.CostPerChild = this.DailyRates.reduce((t, v) => t + v.CostPerChild, 0);
    this.CostPerInfant = this.DailyRates.reduce(
      (t, v) => t + v.CostPerInfant,
      0,
    );
    this.PerPersonCostPerAdult = this.DailyRates.reduce(
      (t, v) => t + v.PerPersonCostPerAdult,
      0,
    );
    this.PerPersonCostPerChild = this.DailyRates.reduce(
      (t, v) => t + v.PerPersonCostPerChild,
      0,
    );
    this.PerPersonCostPerInfant = this.DailyRates.reduce(
      (t, v) => t + v.PerPersonCostPerInfant,
      0,
    );

    this.OriginalBaseRate = this.OriginalDailyRates.reduce(
      (t, v) => t + v.BaseRate,
      0,
    );
    this.OriginalCostPerRoom = this.OriginalDailyRates.reduce(
      (t, v) => t + v.CostPerRoom,
      0,
    );
    this.OriginalCostPerAdult = this.OriginalDailyRates.reduce(
      (t, v) => t + v.CostPerAdult,
      0,
    );
    this.OriginalCostPerChild = this.OriginalDailyRates.reduce(
      (t, v) => t + v.CostPerChild,
      0,
    );
    this.OriginalCostPerInfant = this.OriginalDailyRates.reduce(
      (t, v) => t + v.CostPerInfant,
      0,
    );
    this.OriginalPerPersonCostPerAdult = this.OriginalDailyRates.reduce(
      (t, v) => t + v.PerPersonCostPerAdult,
      0,
    );
    this.OriginalPerPersonCostPerChild = this.OriginalDailyRates.reduce(
      (t, v) => t + v.PerPersonCostPerChild,
      0,
    );
    this.OriginalPerPersonCostPerInfant = this.OriginalDailyRates.reduce(
      (t, v) => t + v.PerPersonCostPerInfant,
      0,
    );
  }

  DailyRates?: NightlyRateDTO[];
  OriginalDailyRates?: NightlyRateDTO[];
  /** @format double */
  BaseRate?: number;
  /** @format int32 */
  BaseNumberOfAdults?: number;
  /** @format double */
  CostPerRoom?: number;
  /** @format double */
  CostPerAdult?: number;
  /** @format double */
  CostPerChild?: number;
  /** @format double */
  CostPerInfant?: number;
  /** @format double */
  PerPersonCostPerAdult?: number;
  /** @format double */
  PerPersonCostPerChild?: number;
  /** @format double */
  PerPersonCostPerInfant?: number;
  /** @format double */
  OriginalBaseRate?: number;
  /** @format double */
  OriginalCostPerRoom?: number;
  /** @format double */
  OriginalCostPerAdult?: number;
  /** @format double */
  OriginalCostPerChild?: number;
  /** @format double */
  OriginalCostPerInfant?: number;
  /** @format double */
  OriginalPerPersonCostPerAdult?: number;
  /** @format double */
  OriginalPerPersonCostPerChild?: number;
  /** @format double */
  OriginalPerPersonCostPerInfant?: number;
  /** @format int32 */
  RatePlanId?: number;
  RatePlanCode?: string;
  RatePlanName?: string;
  RatePlanDescription?: string;
  Details?: string;
  Policy?: PolicyDTO;
  Tags?: TagDTO[];
  CanPayByCompanyInvoice?: boolean;
  UseOccupancyForRatePlan?: boolean;
  /** @format int32 */
  MinimumOccupancy?: number;
  /** @format int32 */
  MaximumOccupancy?: number;
  /** @format int32 */
  AdultMaximumOccupancy?: number;
  /** @format int32 */
  ChildMaximumOccupancy?: number;
  Promotion?: RoomRatePromotionViewDTO;
}

export class RoomTypeAvailability implements RoomTypeAvailabilityDTO {
  constructor(data: RoomTypeAvailabilityDTO) {
    Object.assign(this, data);

    this.LowestRate = -1;
    this.OriginalLowestRate = -1;
  }

  LowestRate: number;
  OriginalLowestRate: number;

  RoomType?: RoomTypeDTO;
  Rates?: RateDTO[];
  /** @format double */

  /** @format date-time */
  DateFrom?: string;
  /** @format date-time */
  DateTo?: string;
}

export interface IDateNumber {
  year: number;
  month: number;
  day: number;
}

export interface IDateRange {
  from: IDateNumber;
  to: IDateNumber;
}

export interface IDateRangeValue {
  from: string;
  to: string;
}

export type IDateUpdateReason =
  | 'add-day'
  | 'remove-day'
  | 'add-range'
  | 'remove-range'
  | 'mask'
  | 'locale'
  | 'year'
  | 'month';

export type IDateUpdateValue = string | Array<unknown> | object | null;

export interface IDateUpdateDetails {
  day: number;
  month: number;
  year: number;
  from?: IDateNumber;
  to?: IDateNumber;
}

export interface IOccupancyRoomDTO {
  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;
}

export interface IRoom {
  roomId: string;
  roomNumber: number;
  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;
}

export interface ISearchRoom extends IRoom {
  isEditing: boolean;
}

export interface RoomTypeIdQuantityChecker {
  (roomTypeId: number): number;
}

export interface IVatSettings {
  vatPercentage: number;
  isStandardTaxExclusive: boolean;
}

export interface IReservationItemUpgrade {
  roomType: RoomTypeUpgradeDTO;
  rate: RateDTO;
  totalUpgradeCost: number;
  totalCostDifference: number;
}

export interface IReservationItem {
  roomId: string;
  roomNumber: number;
  ReservationItemId: number;
  RatePlanId: number;
  RoomTypeId: number;

  DateOfArrival: string;
  DateOfDeparture: string;

  haveReviewedAllGuests: boolean;
  haveReviewedRoom: boolean;
  checkForUpgrades(): void;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  haveSeenUpgrades: boolean;
  hasUpgrades: boolean;

  hasSelectableServices: boolean;
  areServicesLoaded: boolean;
  haveSeenServices: boolean;
  getService(serviceId: number): ReservationItemService;
  Services: ReservationItemService[];
  get hasServices(): boolean;
  get canEditServices(): boolean;
  get canShowEditServices(): boolean;
}

export interface IReservationItemServiceDTO {
  ServiceId: number;
  Quantity: number;
}

export interface IReservationItemService extends IReservationItemServiceDTO {
  ServiceId: number;
  Service: BookingEngineServiceDTO;
  Quantity: number;
  TotalCost: number;
}

function instanceOfBookingEngineServiceDTO(
  data?:
    | PreCheckInReservationItemServiceDTO
    | ReservationItemServiceDTO
    | BookingEngineServiceDTO,
): data is BookingEngineServiceDTO {
  return (
    'ServiceCode' in data &&
    'ServiceName' in data &&
    'ServiceDescription' in data
  );
}

export class ReservationItemService {
  constructor(
    data?:
      | PreCheckInReservationItemServiceDTO
      | ReservationItemServiceDTO
      | BookingEngineServiceDTO,
  ) {
    if (!data) {
      return;
    }
    this.ServiceId = data.ServiceId;
    if (instanceOfBookingEngineServiceDTO(data)) {
      this.Service = data;
    } else {
      this.Service = data.Service;
      this.ReservationItemId = data.ReservationItemId;
      this.Quantity = data.Quantity;
    }
  }

  ReservationItemId: number;
  ServiceId: number;
  Service: BookingEngineServiceDTO;
  Quantity: number;
  TotalCost: number;

  roomId: string;

  justAdded: boolean; // True if the service was just added, and has not been saved to the DB yet.

  get canEdit(): boolean {
    return (
      this.ReservationItemId == null ||
      (this.ReservationItemId != null && this.Service.AllowEditOnBookingEngine)
    );
  }

  toReservationItemServiceDTO(): ReservationItemServiceDTO {
    return {
      ServiceId: this.ServiceId ?? this.Service?.ServiceId,
      Quantity: this.Quantity,
    } as ReservationItemServiceDTO;
  }

  toDTO() {
    return {
      ServiceId: this.ServiceId ?? this.Service?.ServiceId,
      Quantity: this.Quantity,
    };
  }
}

export class ServiceInventory {
  ServiceId: number;
  StartingInventory?: number | null;
  ReservationItems: Array<ReservationItemService> = [];

  get UsedInventory(): number {
    return this.ReservationItems.reduce((a, b) => {
      return a + b.Quantity;
    }, 0);
  }
  get AvailableInventory(): number | null {
    const s = this.StartingInventory;
    if (s == null) {
      return null;
    }
    return s - this.UsedInventory;
  }
}

export class Guest implements GuestDTO {
  constructor(data: GuestDTO, config: IndividualConfigurationDTO) {
    Object.assign(this, data);

    this.haveReviewedGuest = false;
    this.guestId = utils.generateRandomId();

    this.config = config;
  }

  guestId: string;
  config: IndividualConfigurationDTO;

  IndividualId?: number;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  Phone?: string;

  haveReviewedGuest: boolean;

  get fullname(): string {
    return this.FirstName + ' ' + this.LastName;
  }

  get isValid(): boolean {
    return (
      validationUtils.isValidStringValue(
        this.FirstName,
        this.config.FirstNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.LastName,
        this.config.LastNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.Email,
        this.config.EmailRequired,
      ) &&
      validationUtils.isValidStringValue(this.Phone, this.config.PhoneRequired)
    );
  }

  toDTO(): GuestDTO {
    return {
      IndividualId: this.IndividualId,
      FirstName: this.FirstName,
      LastName: this.LastName,
      Email: this.Email,
      Phone: this.Phone,
    } as GuestDTO;
  }
}

export class Booker {
  constructor(data: BookerDTO, config: IndividualConfigurationDTO) {
    Object.assign(this, data);
    this.config = config;
  }

  IndividualId?: number;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  Phone?: string;
  Street?: string;
  City?: string;
  Country: ApiDropdown;
  StateOrCounty?: string;
  ZipCode?: string;

  config: IndividualConfigurationDTO;

  get fullname(): string {
    return this.FirstName + ' ' + this.LastName;
  }

  get contactInformation(): string {
    if (this.Email && this.Phone) {
      return this.Email + ' / ' + this.Phone;
    } else if (this.Email && !this.Phone) {
      return this.Email;
    } else if (!this.Email && this.Phone) {
      return this.Phone;
    }
    return '';
  }

  get areAddressDetailsValid(): boolean {
    return (
      validationUtils.isValidObjectValue(
        this.Country,
        this.config.CountryRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.Street,
        this.config.StreetRequired,
      ) &&
      validationUtils.isValidStringValue(this.City, this.config.CityRequired) &&
      validationUtils.isValidStringValue(
        this.StateOrCounty,
        this.config.StateOrCountyRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.ZipCode,
        this.config.PostCodeRequired,
      )
    );
  }

  get arePersonalDetailsValid(): boolean {
    return (
      validationUtils.isValidStringValue(
        this.FirstName,
        this.config.FirstNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.LastName,
        this.config.LastNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.Email,
        this.config.EmailRequired,
      ) &&
      validationUtils.isValidStringValue(this.Phone, this.config.PhoneRequired)
    );
  }

  get isValid(): boolean {
    return this.arePersonalDetailsValid && this.areAddressDetailsValid;
  }

  toDTO(): BookerDTO {
    return {
      IndividualId: this.IndividualId,
      FirstName: this.FirstName,
      LastName: this.LastName,
      Email: this.Email,
      Phone: this.Phone,
      Street: this.Street,
      City: this.City,
      CountryId: this.Country?.Id,
      StateOrCounty: this.StateOrCounty,
      ZipCode: this.ZipCode,
    } as BookerDTO;
  }
}
